import React, { useState, useEffect } from 'react';
import { IoChatbubbleEllipsesOutline, IoCheckmark, IoCheckmarkDone, IoTime, IoRefresh, IoDownload, IoCamera, IoVideocam, IoTrashOutline, IoDocument, IoEyeOutline, IoClose, IoChevronBack, IoChevronForward } from 'react-icons/io5';

const FILE_ONLY_MESSAGE = 'QmtsoYltwVw';
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
// Consecutive images from the same sender within this window are shown as one collage
const COLLAGE_WINDOW_MS = 2 * 60 * 1000;
const COLLAGE_MAX_TILES = 4;

const hasCaption = (message) => !!message.message && message.message !== FILE_ONLY_MESSAGE;

// Returns image info if the message is an image (uploaded or still uploading), otherwise null
const getMessageImage = (message) => {
  if (message.file) {
    if (!IMAGE_EXTENSIONS.includes(message.file_type?.toLowerCase())) return null;
    return {
      url: `https://buzzinguniverse.com/backend/${message.file}`,
      name: message.file.split('/').pop(),
      uploading: false,
    };
  }
  const attachment = message.attachments?.[0];
  if (attachment && attachment.type === 'image' && attachment.preview) {
    return {
      url: attachment.preview,
      name: attachment.name,
      uploading: message.uploadProgress !== undefined,
      progress: message.uploadProgress,
    };
  }
  return null;
};

// Merge runs of image messages into collage items; everything else stays a single item
const buildRenderItems = (dateMessages) => {
  const items = [];
  dateMessages.forEach(message => {
    const image = getMessageImage(message);
    const last = items[items.length - 1];
    const lastMessage = last?.messages[last.messages.length - 1];

    const canJoin = image && last?.isImageRun &&
      lastMessage.sender_id == message.sender_id &&
      !hasCaption(lastMessage) &&
      Math.abs(new Date(message.created_at) - new Date(lastMessage.created_at)) <= COLLAGE_WINDOW_MS;

    if (canJoin) {
      last.messages.push(message);
      last.images.push(image);
    } else {
      items.push({ isImageRun: !!image, messages: [message], images: image ? [image] : [] });
    }
  });
  return items;
};

const MessageList = ({ messages, currentUser, onResendMessage, onDeleteMessage }) => {
  const [lightbox, setLightbox] = useState(null); // { images, index }

  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(lb => ({ ...lb, index: (lb.index + 1) % lb.images.length }));
      if (e.key === 'ArrowLeft') setLightbox(lb => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = formatDate(message.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  const renderAttachments = (message) => {
    // Check if message has file attachment from server or is uploading
    const hasServerFile = message.file;
    const hasUploadingFile = message.attachments && message.attachments.length > 0;
    
    if (!hasServerFile && !hasUploadingFile) return null;

    // Handle uploading files (temporary attachments)
    if (hasUploadingFile && !hasServerFile) {
      const attachment = message.attachments[0];
      const isImage = attachment.type === 'image';
      const isVideo = attachment.type === 'video';
      const isAudio = attachment.type === 'audio';
      const isAttachPdf = attachment.type === 'pdf';

      return (
        <div className="message-file-attachment uploading">
          {isImage && attachment.preview ? (
            <div className="message-image-container">
              <img
                src={attachment.preview}
                alt={attachment.name}
                className="message-image uploading"
              />
              {message.uploadProgress !== undefined && (
                <div className="upload-progress-overlay">
                  <div className="progress-circle">
                    <div className="progress-text">{message.uploadProgress}%</div>
                    <svg className="progress-ring" width="60" height="60">
                      <circle
                        className="progress-ring-circle"
                        stroke="white"
                        strokeWidth="3"
                        fill="transparent"
                        r="26"
                        cx="30"
                        cy="30"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 26}`,
                          strokeDashoffset: `${2 * Math.PI * 26 * (1 - message.uploadProgress / 100)}`,
                        }}
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="message-document uploading">
              <div className="document-icon">
                {isVideo && <IoVideocam size={24} />}
                {isAudio && <span style={{fontSize: '24px'}}>🎵</span>}
                {isAttachPdf && <IoDocument size={24} />}
                {!isVideo && !isAudio && !isAttachPdf && <IoCamera size={24} />}
              </div>
              <div className="document-details">
                <span className="document-name">{attachment.name}</span>
                <span className="document-type">{attachment.type?.toUpperCase()} file</span>
              </div>
              {message.uploadProgress !== undefined && (
                <div className="upload-progress-bar">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${message.uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-percentage">{message.uploadProgress}%</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Handle server files (completed uploads)
    const fileUrl = `https://buzzinguniverse.com/backend/${message.file}`;
    const fileName = message.file.split('/').pop();
    const fileType = message.file_type?.toLowerCase();
    
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType);
    const isVideo = ['mp4', 'mkv', 'avi', 'mov', 'wmv'].includes(fileType);
    const isAudio = ['mp3', 'wav', 'ogg', 'aac'].includes(fileType);
    const isPdf = fileType === 'pdf';

    return (
      <div className="message-file-attachment">
        {isImage ? (
          <div className="message-image-container">
            <img
              src={fileUrl}
              alt={fileName}
              className="message-image"
              onClick={() => window.open(fileUrl, '_blank')}
            />
            <div className="image-download-overlay">
              <a
                href={fileUrl}
                download={fileName}
                className="download-btn"
                title="Download image"
                onClick={(e) => e.stopPropagation()}
              >
                <IoDownload size={16} />
              </a>
            </div>
          </div>
        ) : isVideo ? (
          <div className="message-video-container">
            <video
              src={fileUrl}
              className="message-video"
              controls
              preload="metadata"
            />
            <div className="video-info">
              <span className="file-name">{fileName}</span>
              <a
                href={fileUrl}
                download={fileName}
                className="download-link"
                title="Download video"
              >
                <IoDownload size={14} />
              </a>
            </div>
          </div>
        ) : (
          <div className="message-document">
            <div className="document-icon">
              {isAudio && <span style={{fontSize: '24px'}}>🎵</span>}
              {isPdf && <IoDocument size={24} />}
              {!isAudio && !isPdf && <IoCamera size={24} />}
            </div>
            <div className="document-details">
              <span className="document-name">{fileName}</span>
              <span className="document-type">{fileType?.toUpperCase()} file</span>
            </div>
            {isPdf ? (
              <div className="document-actions">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="document-download"
                  title="View PDF"
                >
                  <IoEyeOutline size={16} />
                </a>
                <a
                  href={fileUrl}
                  download={fileName}
                  className="document-download"
                  title="Download file"
                >
                  <IoDownload size={16} />
                </a>
              </div>
            ) : (
              <a
                href={fileUrl}
                download={fileName}
                className="document-download"
                title="Download file"
              >
                <IoDownload size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCollage = (images) => {
    const visible = images.slice(0, COLLAGE_MAX_TILES);
    const extraCount = images.length - visible.length;
    const layoutClass = `collage-${Math.min(images.length, COLLAGE_MAX_TILES)}`;

    return (
      <div className={`message-collage ${layoutClass}`}>
        {visible.map((image, i) => {
          const showMore = extraCount > 0 && i === visible.length - 1;
          return (
            <div
              key={i}
              className="collage-tile"
              onClick={() => !image.uploading && setLightbox({ images, index: i })}
            >
              <img
                src={image.url}
                alt={image.name}
                className={image.uploading ? 'uploading' : ''}
              />
              {image.uploading && (
                <div className="collage-progress">{image.progress}%</div>
              )}
              {showMore && (
                <div className="collage-more">+{extraCount}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderLightbox = () => {
    if (!lightbox) return null;
    const { images, index } = lightbox;
    const image = images[index];
    const go = (step) => (e) => {
      e.stopPropagation();
      setLightbox(lb => ({ ...lb, index: (lb.index + step + lb.images.length) % lb.images.length }));
    };

    return (
      <div className="chat-lightbox" onClick={() => setLightbox(null)}>
        <div className="chat-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
          <span className="chat-lightbox-counter">{index + 1} / {images.length}</span>
          <a href={image.url} download={image.name} className="chat-lightbox-btn" title="Download image">
            <IoDownload size={20} />
          </a>
          <button className="chat-lightbox-btn" onClick={() => setLightbox(null)} title="Close">
            <IoClose size={22} />
          </button>
        </div>
        {images.length > 1 && (
          <button className="chat-lightbox-nav prev" onClick={go(-1)} title="Previous">
            <IoChevronBack size={28} />
          </button>
        )}
        <img
          src={image.url}
          alt={image.name}
          className="chat-lightbox-image"
          onClick={(e) => e.stopPropagation()}
        />
        {images.length > 1 && (
          <button className="chat-lightbox-nav next" onClick={go(1)} title="Next">
            <IoChevronForward size={28} />
          </button>
        )}
      </div>
    );
  };

  const messageGroups = groupMessagesByDate(messages);

  if (messages.length === 0) {
    return (
      <div className="no-messages">
        <div className="no-messages-content">
          <div className="no-messages-icon">
            <IoChatbubbleEllipsesOutline size={64} />
          </div>
          <h3>Start chatting now</h3>
          <p>Send a message to start the conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-list">
      {Object.entries(messageGroups).map(([date, dateMessages]) => (
        <div key={date}>
          <div className="date-separator">
            <span>{date}</span>
          </div>
          {buildRenderItems(dateMessages).map((item, index, items) => {
            const message = item.messages[0];
            const lastMessage = item.messages[item.messages.length - 1];
            const isCollage = item.messages.length > 1;
            const isOwn = parseInt(message.sender_id) === currentUser;
            const prevItem = items[index - 1];
            const showAvatar = !isOwn && (
              index === 0 ||
              prevItem.messages[prevItem.messages.length - 1].sender_id !== message.sender_id
            );

            const isSending = item.messages.some(m => m.sending);
            const failedMessages = item.messages.filter(m => m.failed);
            // Caption of a collage lives on its last message
            const captionMessage = isCollage ? lastMessage : message;

            return (
              <div
                key={message.id}
                className={`message ${isOwn ? 'own' : 'other'}`}
              >
                {showAvatar && (
                  <div className="message-avatar">
                    {message.user?.photo ? (
                      <img
                        src={`https://buzzinguniverse.com/backend/${message.user.photo}`}
                        alt="Avatar"
                        className="avatar-img"
                      />
                    ) : (
                      message.user?.first_name?.charAt(0) || 'U'
                    )}
                  </div>
                )}
                <div className={`message-content ${!showAvatar && !isOwn ? 'no-avatar' : ''}`}>
                  <div className={`message-bubble ${failedMessages.length ? 'failed' : ''} ${message.file || isCollage ? 'has-attachment' : ''} ${isCollage ? 'has-collage' : ''}`}>
                    {/* Delete button - only for own messages */}
                    {isOwn && !isSending && onDeleteMessage && (
                      <button
                        className="delete-message-btn"
                        onClick={() => onDeleteMessage(isCollage ? item.messages.map(m => m.id) : message.id)}
                        title={isCollage ? 'Delete images' : 'Delete message'}
                      >
                        <IoTrashOutline size={14} />
                      </button>
                    )}

                    {/* Render file attachment first */}
                    {isCollage ? renderCollage(item.images) : renderAttachments(message)}

                    {/* Render text message if exists and not the special file-only message */}
                    {hasCaption(captionMessage) && (
                      <div className="message-text">
                        <p>{captionMessage.message}</p>
                      </div>
                    )}

                    <div className="message-time">
                      {formatTime(lastMessage.created_at)}
                      {isSending && (
                        <span className="sending">
                          <IoTime size={12} />
                        </span>
                      )}
                      {failedMessages.length > 0 && (
                        <button
                          className="resend-btn"
                          onClick={() => failedMessages.forEach(m => onResendMessage(m))}
                          title="Tap to retry"
                        >
                          <IoRefresh size={12} />
                        </button>
                      )}
                      {isOwn && !isSending && !failedMessages.length && (
                        <span className="read-status">
                          {lastMessage.is_read === '1' ? (
                            <IoCheckmarkDone size={14} />
                          ) : (
                            <IoCheckmark size={14} />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {renderLightbox()}
    </div>
  );
};

export default MessageList;